import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import './AdminAbout.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSave, FaInfoCircle, FaUsers, FaLightbulb, FaRocket, FaPlus, FaTrash } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { safeFetch } from '../utils/apiUtils';

const AdminAbout = () => {
    const [activeTab, setActiveTab] = useState('hero');
    const [isSaving, setIsSaving] = useState(false);
    const [aboutId, setAboutId] = useState(null);

    // State matching the backend API structure
    const [aboutData, setAboutData] = useState({
        hero: {
            titlePart1: 'Building',
            highlight1: 'Technology',
            titlePart2: 'With',
            highlight2: 'Purpose & Integrity',
            description: 'Ethical and purpose-driven digital products designed for human impact. We transform complex data into intuitive solutions that matter.'
        },
        who: {
            title: 'Professional expertise with human-centric values.',
            description: 'Nimai Solutions is a boutique technology consulting firm that bridges the gap between high-level engineering and ethical responsibility...',
            stats: [
                { value: '150+', label: 'IMPACTFUL PROJECTS' },
                { value: '12+', label: 'COUNTRIES REACHED' }
            ]
        },
        philosophy: [
            { title: 'Purpose-Driven', desc: 'Focusing on impact first. We only build what truly adds value to the end user and society.' },
            { title: 'Ethical', desc: 'Transparency and integrity in our code, our pricing, and our client interactions.' },
            { title: 'Scalable', desc: 'Building future-proof architectures that grow effortlessly alongside your organization.' },
            { title: 'Partnership', desc: 'A collaborative approach to co-creation. We don\'t just work for you; we work with you.' }
        ],
        mv: {
            mission: 'To engineer high-performance digital products through an ethical lens, ensuring technology serves as a force for positive change in the world.',
            vision: 'A digital landscape where every interaction is secure, every design is intentional, and every product is built with long-term human impact in mind.'
        }
    });

    useEffect(() => {
        fetchAboutContent();
    }, []);

    const fetchAboutContent = async () => {
        try {
            const { ok, data } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/about-contents', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                }
            });

            let dataToSet = null;
            if (Array.isArray(data)) {
                dataToSet = data[0];
            } else if (data.data) {
                dataToSet = Array.isArray(data.data) ? data.data[0] : data.data;
            }

            if (ok && dataToSet) {
                setAboutId(dataToSet.id);
                setAboutData({
                    hero: {
                        titlePart1: dataToSet.hero_title_1,
                        highlight1: dataToSet.hero_title_2,
                        titlePart2: dataToSet.hero_title_3,
                        highlight2: dataToSet.hero_title_4,
                        description: dataToSet.hero_description
                    },
                    who: {
                        title: dataToSet.who_we_are_title,
                        description: dataToSet.who_we_are_description,
                        stats: dataToSet.who_we_are_stats || []
                    },
                    philosophy: (dataToSet.philosophy_pillars || []).map(p => ({
                        title: p.title,
                        desc: p.description
                    })),
                    mv: {
                        mission: dataToSet.mission_statement,
                        vision: dataToSet.vision_statement
                    }
                });
            }
        } catch (error) {
            console.error('Error fetching about content:', error);
            toast.error('Failed to load about content');
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        const method = aboutId ? 'PUT' : 'POST';
        const url = aboutId
            ? `https://aayushrealproperties.in/nimaibackend/public/api/about-contents/${aboutId}`
            : 'https://aayushrealproperties.in/nimaibackend/public/api/about-contents';

        const payload = {
            hero_title_1: aboutData.hero.titlePart1,
            hero_title_2: aboutData.hero.highlight1,
            hero_title_3: aboutData.hero.titlePart2,
            hero_title_4: aboutData.hero.highlight2,
            hero_description: aboutData.hero.description,
            who_we_are_title: aboutData.who.title,
            who_we_are_description: aboutData.who.description,
            who_we_are_stats: aboutData.who.stats,
            philosophy_pillars: aboutData.philosophy.map(p => ({
                title: p.title,
                description: p.desc
            })),
            mission_statement: aboutData.mv.mission,
            vision_statement: aboutData.mv.vision
        };

        try {
            const { ok, data: result } = await safeFetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (ok) {
                if (!aboutId) setAboutId(result.id);
                toast.success('About page content updated successfully!');
            } else {
                toast.error(result.message || 'Failed to update about content');
            }
        } catch (error) {
            console.error('Error saving about content:', error);
            toast.error('Network error while saving');
        } finally {
            setIsSaving(false);
        }
    };

    const renderHeroTab = () => (
        <div className="tab-pane">
            <div className="admin-form-group">
                <label>Hero Title Structure</label>
                <div className="structure-inputs">
                    <input type="text" value={aboutData.hero.titlePart1} onChange={(e) => setAboutData({ ...aboutData, hero: { ...aboutData.hero, titlePart1: e.target.value } })} placeholder="Part 1" />
                    <input type="text" value={aboutData.hero.highlight1} onChange={(e) => setAboutData({ ...aboutData, hero: { ...aboutData.hero, highlight1: e.target.value } })} placeholder="Highlight 1" className="highlight-input" />
                    <input type="text" value={aboutData.hero.titlePart2} onChange={(e) => setAboutData({ ...aboutData, hero: { ...aboutData.hero, titlePart2: e.target.value } })} placeholder="Part 2" />
                    <input type="text" value={aboutData.hero.highlight2} onChange={(e) => setAboutData({ ...aboutData, hero: { ...aboutData.hero, highlight2: e.target.value } })} placeholder="Highlight 2" className="highlight-input" />
                </div>
            </div>
            <div className="admin-form-group">
                <label>Hero Description</label>
                <textarea rows="4" value={aboutData.hero.description} onChange={(e) => setAboutData({ ...aboutData, hero: { ...aboutData.hero, description: e.target.value } })}></textarea>
            </div>
        </div>
    );

    const renderWhoTab = () => (
        <div className="tab-pane">
            <div className="admin-form-group">
                <label>Section Title</label>
                <input type="text" value={aboutData.who.title} onChange={(e) => setAboutData({ ...aboutData, who: { ...aboutData.who, title: e.target.value } })} />
            </div>
            <div className="admin-form-group">
                <label>Description</label>
                <textarea rows="6" value={aboutData.who.description} onChange={(e) => setAboutData({ ...aboutData, who: { ...aboutData.who, description: e.target.value } })}></textarea>
            </div>
            <div className="admin-form-group">
                <label>Stats</label>
                {aboutData.who.stats.map((stat, idx) => (
                    <div key={idx} className="stat-row">
                        <input type="text" value={stat.value} onChange={(e) => {
                            const newStats = [...aboutData.who.stats];
                            newStats[idx].value = e.target.value;
                            setAboutData({ ...aboutData, who: { ...aboutData.who, stats: newStats } });
                        }} placeholder="Value (e.g. 150+)" />
                        <input type="text" value={stat.label} onChange={(e) => {
                            const newStats = [...aboutData.who.stats];
                            newStats[idx].label = e.target.value;
                            setAboutData({ ...aboutData, who: { ...aboutData.who, stats: newStats } });
                        }} placeholder="Label" />
                    </div>
                ))}
            </div>
        </div>
    );

    const renderPhilosophyTab = () => (
        <div className="tab-pane">
            <div className="cards-management">
                {aboutData.philosophy.map((item, idx) => (
                    <div key={idx} className="admin-card-edit">
                        <div className="card-header">
                            <h4>Feature {idx + 1}</h4>
                            <button className="remove-item" onClick={() => {
                                const newPhi = aboutData.philosophy.filter((_, i) => i !== idx);
                                setAboutData({ ...aboutData, philosophy: newPhi });
                            }}><FaTrash /></button>
                        </div>
                        <div className="admin-input-row">
                            <label>Title</label>
                            <input type="text" value={item.title} onChange={(e) => {
                                const newPhi = [...aboutData.philosophy];
                                newPhi[idx].title = e.target.value;
                                setAboutData({ ...aboutData, philosophy: newPhi });
                            }} placeholder="Enter pillar title..." />
                        </div>
                        <div className="admin-input-row">
                            <label>Description</label>
                            <textarea rows="3" value={item.desc} onChange={(e) => {
                                const newPhi = [...aboutData.philosophy];
                                newPhi[idx].desc = e.target.value;
                                setAboutData({ ...aboutData, philosophy: newPhi });
                            }} placeholder="Enter pillar description..."></textarea>
                        </div>
                    </div>
                ))}
                <button className="add-card-btn" onClick={() => {
                    setAboutData({ ...aboutData, philosophy: [...aboutData.philosophy, { title: '', desc: '' }] });
                }}><FaPlus /> Add Philosophy Pillar</button>
            </div>
        </div>
    );

    const renderMVTab = () => (
        <div className="tab-pane">
            <div className="admin-form-group">
                <label>Our Mission Statement</label>
                <textarea rows="4" value={aboutData.mv.mission} onChange={(e) => setAboutData({ ...aboutData, mv: { ...aboutData.mv, mission: e.target.value } })}></textarea>
            </div>
            <div className="admin-form-group">
                <label>Our Vision Statement</label>
                <textarea rows="4" value={aboutData.mv.vision} onChange={(e) => setAboutData({ ...aboutData, mv: { ...aboutData.mv, vision: e.target.value } })}></textarea>
            </div>
        </div>
    );

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Manage <span>About Page</span></h1>
                        <p>Customize the content of your professional narrative.</p>
                    </div>
                    <button className="save-btn" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Updating...' : <><FaSave /> Save Changes</>}
                    </button>
                </header>

                <div className="admin-tabs">
                    <button className={activeTab === 'hero' ? 'active' : ''} onClick={() => setActiveTab('hero')}>
                        <FaInfoCircle /> Hero
                    </button>
                    <button className={activeTab === 'who' ? 'active' : ''} onClick={() => setActiveTab('who')}>
                        <FaUsers /> Who We Are
                    </button>
                    <button className={activeTab === 'philosophy' ? 'active' : ''} onClick={() => setActiveTab('philosophy')}>
                        <FaLightbulb /> Philosophy
                    </button>
                    <button className={activeTab === 'mv' ? 'active' : ''} onClick={() => setActiveTab('mv')}>
                        <FaRocket /> Mission/Vision
                    </button>
                </div>

                <div className="tab-content-wrapper">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            {activeTab === 'hero' && renderHeroTab()}
                            {activeTab === 'who' && renderWhoTab()}
                            {activeTab === 'philosophy' && renderPhilosophyTab()}
                            {activeTab === 'mv' && renderMVTab()}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </main>
        </div>
    );
};

export default AdminAbout;
